import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  Slice,
} from '@reduxjs/toolkit'
import { db } from 'fb/index'
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  DocumentReference,
  doc,
  addDoc,
  updateDoc,
} from 'firebase/firestore'
import { fetchIngredientCategories } from 'api/masterApi'
import { isRestMasterEnabled } from 'api/featureFlags'

interface MaterialCategoryState {
  materialCategories: MaterialCategory[]
}
interface MaterialCategory {
  id: string
  name: string
  is_disabled: boolean
  material_category_ref: DocumentReference
}

export type MaterialCategoryType = MaterialCategory

const initialState = {
  materialCategories: [],
} as MaterialCategoryState

export const getMaterialCategories = createAsyncThunk<MaterialCategory[]>(
  'materialCategory/getMaterialCategories',
  async () => {
    // Phase 3: REACT_APP_USE_REST_MASTER=true のとき daseruno-api から取得
    if (isRestMasterEnabled()) {
      const categories = await fetchIngredientCategories()
      return categories
        .filter((c) => c.firestore_document_id) // firestore_document_id が無いレコードは除外
        .map((c) => ({
          id: c.firestore_document_id!,
          name: c.label_name,
          is_disabled: c.is_disabled,
          // DocumentReference を Firestore doc ID から再構築する。
          // これにより既存コンポーネントの material_category_ref.id 参照が壊れない。
          // Firestore への読み取りクエリは行われない (DocumentRef の生成だけ)。
          material_category_ref: doc(
            db,
            'material_categories',
            c.firestore_document_id!,
          ),
        }))
    }

    // フォールバック: 従来の Firestore 読み取り
    const q = query(
      collection(db, 'material_categories'),
      where('is_disabled', '==', false),
      orderBy('order'),
    )
    const querySnapshot = await getDocs(q)
    const materialCategories: MaterialCategory[] = []
    await Promise.all(
      querySnapshot.docs.map(async (document) => {
        const materialCategory = document.data()
        materialCategories.push({
          id: document.id,
          name: materialCategory.name,
          is_disabled: materialCategory.is_disabled,
          material_category_ref: materialCategory.material_category_ref,
        })
      }),
    )
    return materialCategories
  },
)

// createMaterialCategories は管理者向けの初期データ投入用 thunk。
// Firestore に直接書き込むため REST 切替対象外。
export const createMaterialCategories = createAsyncThunk<boolean>(
  'materialCategory/createMaterialCategories',
  async () => {
    const materialCategoryRef = collection(db, 'material_categories')
    const nameList = [
      '小麦粉・雑穀粉',
      'バター・乳製品・油脂・卵',
      'イースト・酵母',
      '砂糖・はちみつ・ジャム',
      '塩・スパイス・ハーブ',
      'チョコレート・ココア',
      'ナッツ(アーモンド・くるみ等)',
      '栗・芋・かぼちゃ・シード',
      'ドライフルーツ・野菜・果物',
      '精肉・肉加工品・ハム・ソーセージ',
      '魚介類・海産物・水産加工品',
      '酒・リキュール・水',
      '膨張剤・香料・色素・凝固剤・添加物・改良剤',
      '調味料',
      'フィリング',
      'その他',
    ]
    await Promise.all(
      nameList.map(async (name, index) => {
        const createData = {
          name: name,
          is_disabled: false,
          order: index,
        }
        const docRef: DocumentReference = await addDoc(
          materialCategoryRef,
          createData,
        )
        const materialCategoryDoc = doc(db, 'material_categories', docRef.id)
        const updateData = {
          material_category_ref: materialCategoryDoc,
        }
        await updateDoc(materialCategoryDoc, updateData)
      }),
    )
    return true
  },
)

export const materialCategorySlice: Slice<
  MaterialCategoryState,
  {},
  'materialCategory'
> = createSlice({
  name: 'materialCategory',
  initialState,
  reducers: {},
  extraReducers: {
    [getMaterialCategories.pending.type]: () => {},
    [getMaterialCategories.fulfilled.type]: (
      state,
      action: PayloadAction<MaterialCategory[]>,
    ) => {
      state.materialCategories = action.payload
    },
    [getMaterialCategories.rejected.type]: () => {},
  },
})

export default materialCategorySlice.reducer
